import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { environment } from '@src/environments/environment';
import { SingleViewStateRecord } from '@src/suite/common/data-models/records/single-viewstate-record';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { selectCurrentApplication } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { getDatabasesOptions } from '@src/suite/common/utils/viewstate-utils';
import { WireApiActions } from '@src/suite/discover/store/actions/discover.actions';
import { LiveSearchService } from '@src/suite/live/services/live-search.service';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { exhaustMap, forkJoin, from, map, of, switchMap, takeUntil, timer } from 'rxjs';

import { ListActions } from './list.actions';
import { selectListGuids } from './list.selectors';

@Injectable()
export class ListEffects {
    private actions$ = inject(Actions);
    private store = inject(Store);
    private playbookSearchService = inject(PlaybookSearchService);
    private liveSearchService = inject(LiveSearchService);
    private nomWebServicesJson = inject(NomWebServicesJson);
    private globalTablesService = inject(GlobalTablesService);

    setCurrentViewStateSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ListActions.startPolling),
            switchMap(() => {
                return timer(0, environment.solr_poll_interval_seconds ? 10000 : environment.solr_poll_interval_seconds * 1000).pipe(
                    takeUntil(
                        this.actions$.pipe(
                            ofType(
                                ViewStateActions.setCurrentViewState,
                                ViewStateActions.viewStateChanged,
                                ViewStateActions.addViewStateFilters,
                                ViewStateActions.removeViewStateFilter,
                                ViewStateActions.updateViewStateFilter,
                                ViewStateActions.adjustDateRange
                            )
                        )
                    )
                );
            }),
            concatLatestFrom(() => [this.store.select(selectCurrentApplication), this.store.select(selectCurrentViewState), this.store.select(selectListGuids)]),
            switchMap(([_, application, vs, currentListGuids]) => {
                if (!vs) {
                    return of(WireApiActions.stopPolling({ restart: false, vs: null, interval: null }));
                } else {
                    if (application === 'discover') {
                        return of(ApplicationActions.noOpAction());
                    }
                    if (application === 'live') {
                        if (vs instanceof SingleViewStateRecord) {
                            const databases = getDatabasesOptions(
                                vs,
                                this.nomWebServicesJson.PSTServer,
                                this.nomWebServicesJson.HelloResults.HomeProgram,
                                this.globalTablesService.AllGroups,
                                this.globalTablesService.AllGroupsArray
                            );
                            return forkJoin(
                                databases.map(database => {
                                    return from(this.liveSearchService.getFilteredResultsGuids(vs, database));
                                })
                            ).pipe(
                                exhaustMap(res => {
                                    const guids = new Set<string>();
                                    res.forEach(guidsSet => {
                                        if (guidsSet.status && guidsSet.data?.length) {
                                            guidsSet.data.forEach(guid => {
                                                guids.add(guid.guid);
                                            });
                                        }
                                    });
                                    const newListGuids = Array.from(guids).filter(guid => !currentListGuids.includes(guid));
                                    return of(ListActions.addNewListGuids({ listGuids: newListGuids }));
                                })
                            );
                        }
                    }
                    if (application === 'playbook') {
                        return from(this.playbookSearchService.getFilteredResultsGuids(vs)).pipe(
                            map(listGuids => {
                                let newListGuids = [];
                                if (listGuids.status && listGuids.data?.length) {
                                    newListGuids = listGuids.data.map(guid => guid.guid).filter(guid => !currentListGuids.includes(guid));
                                }
                                return ListActions.addNewListGuids({ listGuids: newListGuids });
                            })
                        );
                    }
                    return of(ApplicationActions.noOpAction());
                }
            })
        );
    });
}
